import { useCallback, useContext, useEffect, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getExternalContractDetails } from "../../../../setup/api/services/contract";
import {
  ESenderRole,
  IContractDetailsResponseData,
  UserProfile,
} from "../../../../setup/api/services/contract/models";
import { ROUTES } from "../../../../setup/constants";
import { enrichPartiesProfilesPhoneNumbers } from "../../../../setup/util/helpers";
import { buildContractShareLink } from "../../../../setup/util/contract";
import {
  buildTwoPartyContractRoute,
  isTwoPartyEditable,
} from "../../../../setup/util/twoPartyContract";
import StatusStepper from "../../../../components/contract/statusStepper";
import { AuthConfig } from "../../../../setup/context/config/authConfig";
import { ContractContext } from "../../../../providers/contract";
import { EPARTY_STATUS } from "../../../../enums";
import { GET_PARTY_STATUSES } from "../../../../components/contract/payeeTable/constants";
import PDFComponent from "../../../../components/common/PDFComponent";
import Modal from "../../../../components/common/Modal";
import Spinner from "../../../../components/common/Spinner";
import { ReactComponent as TwoPartyIcon } from "../../../../assets/images/svg/twoPartyContract/icon-users-2.svg";
import Amount from "../amount";
import {
  getSellerStatus,
  getSellerSubStatus,
  sellerCanShare,
} from "./sellerStatus";
import styles from "./details.module.scss";

/** "+966561110016" → "561110016", as the table shows Saudi mobiles. */
const nationalPhone = (phone?: string | number) =>
  `${phone ?? ""}`.replace(/^\+?966/, "") || "-";

const partyName = (
  party: Partial<UserProfile> | undefined,
  profiles: Partial<UserProfile>[] | undefined
) => {
  const profile = profiles?.find(
    (entry) =>
      (!!party?.phoneNumber && entry?.phoneNumber === party.phoneNumber) ||
      (party?.userId != null &&
        entry?.userIdentification != null &&
        String(entry.userIdentification) === String(party.userId))
  );
  return (
    party?.sudoName ||
    party?.companyName ||
    profile?.profile?.fullName ||
    profile?.sudoName ||
    "-"
  );
};

interface ITableProps {
  title: string;
  columns: string[];
  cells: React.ReactNode[];
}

const PartyTable = ({ title, columns, cells }: ITableProps) => (
  <section className={styles.section}>
    <h2 className={styles.sectionTitle}>{title}</h2>
    <div className={styles.table} role="table">
      <div className={styles.tableHead} role="row">
        {columns.map((column) => (
          <span key={column} role="columnheader">
            {column}
          </span>
        ))}
      </div>
      <div className={styles.tableRow} role="row">
        {cells.map((cell, index) => (
          <span key={index} role="cell">
            {cell}
          </span>
        ))}
      </div>
    </div>
  </section>
);

/**
 * A two-party transaction's details (WAF-3235, state 7): the C2C stepper and status bar (both
 * ported from Waffy-web-next) with the seller's C2C action (share, while waiting for payment) and
 * "تعديل" until the buyer pays; the buyer, the seller (the signed-in organization) and Waffy as
 * broker; and the attachments.
 */
const TwoPartyDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { actions } = useContext(ContractContext);
  const [contract, setContract] = useState<IContractDetailsResponseData>();
  const [copied, setCopied] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>();

  const fetchContract = useCallback(
    async (contractId: string) => {
      try {
        const response = await getExternalContractDetails(contractId);
        setContract(enrichPartiesProfilesPhoneNumbers(response?.data));
      } catch (error) {
        console.error(error);
        navigate(ROUTES.CONTRACT_NOT_FOUND);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (id) fetchContract(id);
  }, [fetchContract, id]);

  const shareLink = contract ? buildContractShareLink(contract) : "";

  const share = useCallback(async () => {
    if (navigator?.share) {
      try {
        await navigator.share({ url: shareLink });
        return;
      } catch (error) {
        if ((error as DOMException)?.name === "AbortError") return;
      }
    }
    try {
      await navigator?.clipboard?.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      console.error(error);
    }
  }, [shareLink]);

  if (!contract) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    );
  }

  const status = getSellerStatus(contract);
  const subStatus = getSellerSubStatus(contract);

  const buyer = contract.parties?.find(
    (party) => party?.role === ESenderRole.CUSTOMER
  );
  const seller = contract.parties?.find(
    (party) => party?.role === ESenderRole.PROVIDER
  );
  const sellerProfile = contract.partiesProfiles?.PROVIDER?.[0];

  // Waffy's fee comes out of the seller's amount (feePayee PROVIDER).
  const fee = seller?.fee || contract.fee || 0;
  const value = contract.itemPrice ?? buyer?.amount ?? 0;
  const sellerNet = (seller?.amount ?? value) - fee;

  const partyStatuses = GET_PARTY_STATUSES(t);
  const buyerStatus = buyer?.status as EPARTY_STATUS | undefined;
  const joined =
    !!buyerStatus &&
    buyerStatus !== EPARTY_STATUS.INVITED &&
    !!partyStatuses[buyerStatus];
  const statusChip = joined ? (
    <span
      className={styles.statusChip}
      style={{
        background: partyStatuses[buyerStatus as EPARTY_STATUS]?.color,
        color: partyStatuses[buyerStatus as EPARTY_STATUS]?.textColor,
      }}>
      {partyStatuses[buyerStatus as EPARTY_STATUS]?.title}
    </span>
  ) : (
    <span className={classNames(styles.statusChip, styles.notJoined)}>
      {t("lbl_not_joined_yet")}
    </span>
  );

  const attachments = contract.itemDetail?.attachments || [];
  const images = contract.itemDetail?.images || [];
  const canEdit =
    isTwoPartyEditable(contract) &&
    !!AuthConfig.getUserInfo()?.authorities?.some((role) =>
      [
        "CLIENT_ADMIN_UPDATE_CONTRACT",
        "CLIENT_ADMIN",
        "ORGANIZATION_OWNER",
      ].includes(role)
    );

  return (
    <div className={styles.page}>
      <div className={styles.topRow}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => {
            actions.resetContractState();
            if (location.state?.from) navigate(location.state.from);
            else navigate(ROUTES.CONTRACTS_LIST);
          }}>
          <span aria-hidden>→</span>
          {t("lbl_go_back_short")}
        </button>
        <span className={styles.kindChip}>
          <TwoPartyIcon />
          {t("lbl_two_party_transaction")}
        </span>
      </div>

      <h1 className={styles.title}>{contract.itemDetail?.title}</h1>
      {contract.itemDetail?.description && (
        <p className={styles.description}>{contract.itemDetail.description}</p>
      )}

      <div className={styles.stageCard}>
        <StatusStepper contract={contract} />

        <div className={styles.statusRow}>
          {status && (
            <div className={styles.statusInfo}>
              <img src={status.img} alt="" className={styles.statusIcon} />
              <div>
                <p className={styles.statusTitle}>{t(status.title)}</p>
                <p className={styles.statusDescription}>
                  {t(status.description)}
                </p>
              </div>
            </div>
          )}
          <div className={styles.actions}>
            {canEdit && (
              <button
                type="button"
                className={styles.editButton}
                onClick={() =>
                  navigate(
                    buildTwoPartyContractRoute(contract.parentId, contract.id)
                  )
                }>
                <img src="/assets/images/svg/edit.svg" alt="" />
                {t("lbl_edit")}
              </button>
            )}
            {sellerCanShare(contract) && (
              <button
                type="button"
                className={styles.shareButton}
                onClick={share}>
                {copied ? t("lbl_copied") : t("lbl_share_transaction")}
              </button>
            )}
          </div>
        </div>
        {subStatus && (
          <div
            className={styles.subStatus}
            style={{ backgroundColor: subStatus.background }}>
            <img src={subStatus.img} alt="" />
            <div>
              <p className={styles.subStatusTitle}>{t(subStatus.title)}</p>
              <p className={styles.subStatusDescription}>
                {t(subStatus.description)}
              </p>
            </div>
          </div>
        )}
      </div>

      <PartyTable
        title={t("lbl_buyer_section")}
        columns={[
          t("lbl_name"),
          t("lbl_mobile_number"),
          t("lbl_value"),
          t("lbl_status"),
        ]}
        cells={[
          partyName(buyer, contract.partiesProfiles?.CUSTOMER),
          <bdi key="phone">{nationalPhone(buyer?.phoneNumber)}</bdi>,
          <Amount key="amount" value={buyer?.amount ?? value} dark />,
          statusChip,
        ]}
      />

      <PartyTable
        title={t("lbl_seller_section")}
        columns={[t("lbl_name"), t("lbl_mobile_number"), t("lbl_value"), ""]}
        cells={[
          `${partyName(seller, contract.partiesProfiles?.PROVIDER)} ${t(
            "lbl_you_suffix"
          )}`,
          <bdi key="phone">
            {nationalPhone(seller?.phoneNumber || sellerProfile?.phoneNumber)}
          </bdi>,
          <Amount key="amount" value={sellerNet} dark />,
          "",
        ]}
      />

      <PartyTable
        title={t("lbl_broker_section")}
        columns={[t("lbl_name"), t("lbl_fees_column"), "", ""]}
        cells={[t("lbl_waffy"), <Amount key="fee" value={fee} dark />, "", ""]}
      />

      {(attachments.length > 0 || images.length > 0) && (
        <section className={styles.section}>
          <h2 className={styles.attachmentsTitle}>{t("lbl_attachments")}</h2>
          <div className={styles.files}>
            {attachments.map((attachment) => (
              <PDFComponent
                key={attachment}
                isHorizontalPDF
                fileUrl={attachment}
              />
            ))}
          </div>
          {images.length > 0 && (
            <div className={styles.images}>
              {images.map((image) => (
                <button
                  key={image}
                  type="button"
                  className={styles.imageButton}
                  onClick={() => setPreviewImage(image)}>
                  <img src={image} alt="" />
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      <Modal
        visible={!!previewImage}
        onCancel={() => setPreviewImage(undefined)}
        customModalClassName={styles.previewModal}>
        {previewImage && (
          <img src={previewImage} alt="" className={styles.previewImage} />
        )}
      </Modal>
    </div>
  );
};

export default TwoPartyDetails;
